import { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { MapContainer, TileLayer, useMap, Marker, Popup, useMapEvents } from 'react-leaflet';
import "leaflet/dist/leaflet.css";
import * as L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { DATABASE_URL, API_KEY } from "../config/globals";
import '../styles/App.css';


// Tell TS to ignore type checking for _getIconUrl
delete (L.Icon.Default.prototype as any)._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const myIcon = L.icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/25/25694.png",
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

const DEFAULT_POSITION: [number, number] = [25.7617, -80.1918]; // Miami, FL

const NewPinMarker: React.FC<{
  position: [number, number] | null;
  setPosition: (pos: [number, number]) => void;
  userName?: string;
  imgUrl?: string;
  date?: string;
  }> = ({ position, setPosition, userName, imgUrl, date }) => {
    useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  if (!position) return null;

  let newDate: Date | undefined = undefined;
  if (date) {
    newDate = new Date(date)
  }
  return (
    <Marker position={position}>
      <Popup>
        New Fish Marker: <br /> {position[0].toFixed(4)}, {position[1].toFixed(4)}
        <br></br>
        <strong>{userName}</strong>
        
        {imgUrl && <img src={imgUrl} className="pt-2 pb-2"></img>}
        {newDate && <>Uploaded on {newDate.toLocaleString()}</>}
      </Popup>
    </Marker>
  );
};

function ResizeFix() {
  const map = useMap();
  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 100); // slight delay ensures container is fully rendered
  }, [map]);
  return null;
}

type MapProps = {
  onAction?: (pin: [number, number]) => void;
  actionLabel?: string;
  pinEnabled?: boolean;
  flyTo?: { lat: number; lng: number } | null;
  userName?: string | undefined;
  imgUrl?: string | undefined;
  date?: string | undefined;
};

function Map({ onAction = () => console.log("Default action"), actionLabel = "", pinEnabled = false, flyTo = null, userName = undefined, imgUrl = undefined, date = undefined }: MapProps) {
  const [homePosition, setHomePosition] = useState(DEFAULT_POSITION)
  const [newPinPosition, setNewPinPosition] = useState<[number, number]>([51.505, -0.09]);
  const [position, setPosition] = useState(DEFAULT_POSITION)
  const [pins, setPins] = useState<any[]>([]);
  const [mostRecentPin, setMostRecentPin] = useState<any | null>(null);


  async function fetchPins() {
    try {
      const res = await fetch(DATABASE_URL+"/api/pins");
      const data = await res.json();
      setPins(data);
      setMostRecentPin(pins[Math.floor(Math.random() * pins.length)])
      console.log("RETRIEVED PINS: "+data)
    } catch (err) {
      console.error("❌ Error loading pins:", err);
    }
  }
  
  useEffect(() => {
    fetchPins();
  }, []);
  

  function LocationMarker() {
    
    const map = useMapEvents({
      locationfound(e) {
        
      let target = e.latlng;

      if (flyTo && typeof flyTo.lat === "number" && typeof flyTo.lng === "number") {
        target = L.latLng(flyTo.lat, flyTo.lng);
      }

      if (target) {
        map.flyTo(target, 14);
      }
        setHomePosition([e.latlng.lat, e.latlng.lng])
      },
    });
    
    useEffect(() => {
      if (homePosition === DEFAULT_POSITION) {
        map.locate();
      }
    }, [map, homePosition]);

    return homePosition === null ? null : (
      
      <Marker position={homePosition} icon = {myIcon}>
        <Popup>You are here</Popup>
      </Marker>
    );
  }

  function BackToHomeControl() {
    const map = useMapEvents({
      locationfound(e) {
        setPosition([e.latlng.lat, e.latlng.lng]);
        L.circle(e.latlng, { radius: e.accuracy }).addTo(map);
      },
    });


    useEffect(() => {
      const backHomeControl = (L.control as unknown as (opts: L.ControlOptions) => L.Control)( { position: 'topright' });

      backHomeControl.onAdd = function () {
        const button = L.DomUtil.create('button', 'leaflet-bar leaflet-control leaflet-control-custom');
        button.innerHTML = '🏠';
        button.title = 'Back to Home';
        button.style.backgroundColor = 'white';
        button.style.width = '34px';
        button.style.height = '34px';
        button.style.cursor = 'pointer';

        button.onclick = () => {
          map.flyTo(homePosition, 13)
        };

        return button;
      };

      backHomeControl.addTo(map);

       return () => {
        backHomeControl.remove();
       };
    }, [map]);


    useEffect(() => {
      const luckyControl = (L.control as unknown as (opts: L.ControlOptions) => L.Control)( { position: 'topright' });

      luckyControl.onAdd = function () {
        const button = L.DomUtil.create('button', 'leaflet-bar leaflet-control leaflet-control-custom');
        button.innerHTML = '🍀';
        button.title = "I'm feeling lucky";
        button.style.backgroundColor = 'white';
        button.style.width = '34px';
        button.style.height = '34px';
        button.style.cursor = 'pointer';

        button.onclick = () => {
          if (mostRecentPin != null) {
            let newPin = mostRecentPin
          
            while (newPin == mostRecentPin) {
              newPin = pins[Math.floor(Math.random() * pins.length)]
            }
            setMostRecentPin(newPin);
            map.flyTo(newPin.location, 13)
          } else {
            window.alert("Random pin not found.");
          }

        };

        return button;
      };

      luckyControl.addTo(map);

       return () => {
        luckyControl.remove();
       };
    }, [map]);

    return null;
  }

  return (
    <div>
      <MapContainer className="rounded-lg" center={DEFAULT_POSITION} zoom={13} scrollWheelZoom={true} style={{ height: "500px", width: "100%", cursor: "pointer" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" 
        />
        <ResizeFix />
        {pins && pins.length > 0 && pins
        .map((pin) => (
          <Marker key={pin._id} position={[pin.location.lat, pin.location.lng]}>
          <Popup closeButton={true} autoPan={false} className="leaflet-popup-noevents">
          <div>
            <strong>Uploaded by {pin.owner.displayName}</strong>
            <br />
            <img src={pin.imgUrl} className="pt-2 pb-2"></img>
            {pin.date && <>on {new Date(pin.date).toLocaleString()}</>}
          </div>
          </Popup>
          </Marker>
        ))}
        <LocationMarker></LocationMarker>

        {pinEnabled && 
        <>
          <NewPinMarker position={newPinPosition} setPosition={setNewPinPosition} imgUrl={imgUrl} userName={userName} date={date}/>
        </>}
        
        <BackToHomeControl />
        
      </MapContainer>
      {actionLabel &&
      <>
        <div className="p-5 flex justify-center">
          <button onClick={() => onAction(newPinPosition)} type="button" className="text-white bg-blue-700 hover:bg-blue-800 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">
            {actionLabel}
            <svg className="-rotate-90 w-3.5 h-3.5 ms-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 5h12m0 0L9 1m4 4L9 9"/>
            </svg>
          </button>
        </div>
      </>}

    </div>
  );
}

export default Map;
